import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { aiApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
    Plus, Trash2, ChevronLeft, Wand2, Send, Sparkles,
    Loader2, Check, Copy, Table, Edit2, Calendar, Clock, X, Download
} from 'lucide-react';

const PlotGrid = ({ book, chapters, scenes, plotNotes, onSelectChapter }) => {
    const { user, refreshUser } = useAuth();
    
    // Dynamic columns - can add/remove/reorder
    const [columns, setColumns] = useState([
        { id: 'chapter', name: 'Chapter', type: 'text', width: 140 },
        { id: 'time', name: 'Time', type: 'time', width: 140 },
        { id: 'plot', name: 'Plot', type: 'textarea', width: 200 },
        { id: 'subplot1', name: 'SubPlot 1', type: 'textarea', width: 160 },
        { id: 'subplot2', name: 'SubPlot 2', type: 'textarea', width: 160 },
        { id: 'subplot3', name: 'SubPlot 3', type: 'textarea', width: 160 }
    ]);
    
    const [timeFormat, setTimeFormat] = useState('day');
    const [gridData, setGridData] = useState([]);
    const [editingHeader, setEditingHeader] = useState(null);
    const [editingCell, setEditingCell] = useState(null);
    const [copied, setCopied] = useState(false);
    
    // AI Query section
    const [aiQuery, setAiQuery] = useState('');
    const [aiResponse, setAiResponse] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const [queryHistory, setQueryHistory] = useState([]);

    // Initialize grid data from chapters
    useEffect(() => {
        const initialData = chapters.map((chapter, idx) => {
            const row = { id: chapter.id };
            columns.forEach(col => {
                if (col.id === 'chapter') row[col.id] = chapter.title || `Chapter ${idx + 1}`;
                else if (col.id === 'time') row[col.id] = chapter.timeline_date || '';
                else if (col.id === 'plot') row[col.id] = chapter.synopsis || '';
                else row[col.id] = '';
            });
            return row;
        });
        if (initialData.length > 0) setGridData(initialData);
    }, [chapters]);

    // Load saved grid data from localStorage
    useEffect(() => {
        const saved = localStorage.getItem(`eskrybe-plot-grid-${book?.id}`);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed.columns) setColumns(parsed.columns);
            if (parsed.timeFormat) setTimeFormat(parsed.timeFormat);
            if (parsed.data) setGridData(parsed.data);
            if (parsed.queryHistory) setQueryHistory(parsed.queryHistory);
        }
    }, [book?.id]);

    // Save to localStorage when data changes
    useEffect(() => {
        if (book?.id) {
            localStorage.setItem(`eskrybe-plot-grid-${book.id}`, JSON.stringify({
                columns,
                timeFormat,
                data: gridData,
                queryHistory
            }));
        }
    }, [gridData, columns, timeFormat, queryHistory, book?.id]);

    const updateCell = (rowIndex, field, value) => {
        setGridData(prev => prev.map((row, idx) => 
            idx === rowIndex ? { ...row, [field]: value } : row
        ));
    };

    const updateColumnName = (colId, newName) => {
        setColumns(prev => prev.map(col => 
            col.id === colId ? { ...col, name: newName } : col
        ));
        setEditingHeader(null);
    };

    const addColumn = () => {
        const newId = `custom_${Date.now()}`;
        const newCol = { id: newId, name: 'New Column', type: 'textarea', width: 150 };
        setColumns(prev => [...prev, newCol]);
        // Add empty values for this column to all rows
        setGridData(prev => prev.map(row => ({ ...row, [newId]: '' })));
        toast.success('Column added');
    };

    const deleteColumn = (colId) => {
        if (['chapter', 'time', 'plot'].includes(colId)) {
            toast.error('Cannot delete core columns');
            return;
        }
        setColumns(prev => prev.filter(col => col.id !== colId));
        setGridData(prev => prev.map(row => {
            const newRow = { ...row };
            delete newRow[colId];
            return newRow;
        }));
        toast.success('Column deleted');
    };

    const moveColumn = (colId, direction) => {
        const currentIndex = columns.findIndex(col => col.id === colId);
        if (currentIndex === -1) return;
        
        const newIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex < 0 || newIndex >= columns.length) return;
        
        const newColumns = [...columns];
        const [movedCol] = newColumns.splice(currentIndex, 1);
        newColumns.splice(newIndex, 0, movedCol);
        setColumns(newColumns);
    };

    const addRow = () => {
        const newRow = { id: `row_${Date.now()}` };
        columns.forEach(col => {
            if (col.id === 'chapter') newRow[col.id] = `Chapter ${gridData.length + 1}`;
            else newRow[col.id] = '';
        });
        setGridData(prev => [...prev, newRow]);
    };

    const deleteRow = (rowIndex) => {
        setGridData(prev => prev.filter((_, idx) => idx !== rowIndex));
    };

    const getTimeFormatPlaceholder = () => {
        switch (timeFormat) {
            case 'year': return 'e.g., 1942';
            case 'month': return 'e.g., March 1942';
            case 'day': return 'e.g., March 15, 1942';
            case 'time': return 'e.g., 3:00 PM, March 15';
            default: return '';
        }
    };

    const copyToClipboard = async () => {
        const headers = columns.map(col => 
            col.type === 'time' ? `${col.name} (${timeFormat})` : col.name
        ).join('\t');

        const rows = gridData.map(row => 
            columns.map(col => row[col.id] || '').join('\t')
        );

        const tsv = `${book?.title || 'Untitled Book'}\n\n${headers}\n${rows.join('\n')}`;

        try {
            await navigator.clipboard.writeText(tsv);
            setCopied(true);
            toast.success('Copied! Paste into Google Sheets or Excel');
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            toast.error('Failed to copy to clipboard');
        }
    };

    const downloadCSV = () => {
        const headers = columns.map(col => 
            col.type === 'time' ? `${col.name} (${timeFormat})` : col.name
        );

        const csvContent = [
            [book?.title || 'Untitled Book'],
            [],
            headers,
            ...gridData.map(row => columns.map(col => row[col.id] || ''))
        ].map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${book?.title || 'plot-grid'}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('CSV downloaded');
    };

    // AI Query handler
    const handleAiQuery = async () => {
        if (!aiQuery.trim()) return;
        
        setAiLoading(true);
        setAiResponse('');
        
        // Build context from grid data
        const gridContext = gridData.map((row, idx) => 
            columns.map(col => `${col.name}: ${row[col.id] || 'empty'}`).join(', ')
        ).join('\n');
        
        try {
            const res = await aiApi.write({
                prompt: `You are a writing assistant helping with plot development. Here is the current plot grid for "${book?.title || 'the book'}":\n\n${gridContext}\n\nWriter's question: ${aiQuery}\n\nProvide helpful, specific advice for their story.`,
                context: `Book: ${book?.title}, Genre: ${book?.genre}`,
                assist_type: 'story_help'
            });
            
            const response = res.data.response || res.data.suggestion || 'No response';
            setAiResponse(response);
            
            // Add to history
            setQueryHistory(prev => [{
                id: Date.now(),
                query: aiQuery,
                response,
                timestamp: new Date().toISOString()
            }, ...prev].slice(0, 10)); // Keep last 10
            
            refreshUser();
            setAiQuery('');
        } catch (error) {
            if (error.response?.status === 402) {
                toast.error('Insufficient credits');
            } else {
                toast.error('AI query failed');
            }
        } finally {
            setAiLoading(false);
        }
    };

    return (
        <div className="p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-serif text-2xl font-semibold">Plot Grid</h2>
                    <p className="text-muted-foreground mt-1">Plan and organize your story structure</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <select
                            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                            value={timeFormat}
                            onChange={(e) => setTimeFormat(e.target.value)}
                            data-testid="time-format-select"
                        >
                            <option value="year">Year</option>
                            <option value="month">Month</option>
                            <option value="day">Day</option>
                            <option value="time">Time</option>
                        </select>
                    </div>
                    
                    <Button variant="outline" onClick={copyToClipboard} data-testid="copy-grid-btn">
                        {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                        {copied ? 'Copied!' : 'Copy for Sheets'}
                    </Button>
                    
                    <Button variant="outline" onClick={downloadCSV} data-testid="download-csv-btn">
                        <Download className="h-4 w-4 mr-2" />
                        CSV
                    </Button>
                    
                    <Button variant="outline" onClick={addColumn} data-testid="add-column-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Column
                    </Button>
                    
                    <Button onClick={addRow} data-testid="add-row-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Row
                    </Button>
                </div>
            </div>

            {/* Book Title Header */}
            <div className="mb-4 p-3 bg-accent/10 rounded-lg border border-accent/20">
                <h3 className="font-serif text-lg font-semibold text-center">{book?.title || 'Untitled Book'}</h3>
            </div>

            {/* Grid Table */}
            <div className="border rounded-lg overflow-hidden bg-background">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="plot-grid-table">
                        <thead>
                            <tr className="bg-muted/50 border-b">
                                {columns.map((col, colIndex) => (
                                    <th key={col.id} className="p-2 text-left font-semibold" style={{ minWidth: col.width }}>
                                        <div className="space-y-1">
                                            {/* Column Header */}
                                            {editingHeader === col.id ? (
                                                <input
                                                    type="text"
                                                    className="w-full px-2 py-1 border rounded bg-background text-sm"
                                                    value={col.name}
                                                    onChange={(e) => setColumns(prev => prev.map(c => 
                                                        c.id === col.id ? { ...c, name: e.target.value } : c
                                                    ))}
                                                    onBlur={() => setEditingHeader(null)}
                                                    onKeyDown={(e) => e.key === 'Enter' && setEditingHeader(null)}
                                                    autoFocus
                                                />
                                            ) : (
                                                <div 
                                                    className="flex items-center gap-1 cursor-pointer hover:text-accent"
                                                    onClick={() => setEditingHeader(col.id)}
                                                >
                                                    <span className="truncate">{col.name}</span>
                                                    {col.type === 'time' && (
                                                        <span className="text-xs text-muted-foreground">({timeFormat})</span>
                                                    )}
                                                    <Edit2 className="h-3 w-3 opacity-50 flex-shrink-0" />
                                                </div>
                                            )}
                                            
                                            {/* Column Controls */}
                                            <div className="flex items-center gap-1">
                                                <button
                                                    className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground disabled:opacity-30"
                                                    onClick={() => moveColumn(col.id, 'left')}
                                                    disabled={colIndex === 0}
                                                    title="Move left"
                                                >
                                                    <ChevronLeft className="h-3 w-3" />
                                                </button>
                                                <button
                                                    className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground disabled:opacity-30"
                                                    onClick={() => moveColumn(col.id, 'right')}
                                                    disabled={colIndex === columns.length - 1}
                                                    title="Move right"
                                                >
                                                    <ChevronLeft className="h-3 w-3 rotate-180" />
                                                </button>
                                                {!['chapter', 'time', 'plot'].includes(col.id) && (
                                                    <button
                                                        className="p-1 hover:bg-destructive/10 rounded text-muted-foreground hover:text-destructive"
                                                        onClick={() => deleteColumn(col.id)}
                                                        title="Delete column"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </th>
                                ))}
                                <th className="p-2 w-12"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {gridData.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length + 1} className="p-12 text-center">
                                        <Table className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                                        <h3 className="font-serif font-semibold mb-2">No plot data yet</h3>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            Add rows to start planning your story structure
                                        </p>
                                        <Button onClick={addRow}>
                                            <Plus className="h-4 w-4 mr-2" />
                                            Add First Row
                                        </Button>
                                    </td>
                                </tr>
                            ) : (
                                gridData.map((row, rowIndex) => (
                                    <tr key={row.id} className="border-b hover:bg-muted/30 transition-colors">
                                        {columns.map((col) => (
                                            <td key={col.id} className="p-2">
                                                {editingCell === `${rowIndex}-${col.id}` ? (
                                                    col.type === 'textarea' ? (
                                                        <textarea
                                                            className="w-full px-2 py-1 border rounded bg-background min-h-[60px] resize-y text-sm"
                                                            value={row[col.id] || ''}
                                                            onChange={(e) => updateCell(rowIndex, col.id, e.target.value)}
                                                            onBlur={() => setEditingCell(null)}
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            className="w-full px-2 py-1 border rounded bg-background text-sm"
                                                            value={row[col.id] || ''}
                                                            onChange={(e) => updateCell(rowIndex, col.id, e.target.value)}
                                                            onBlur={() => setEditingCell(null)}
                                                            onKeyDown={(e) => e.key === 'Enter' && setEditingCell(null)}
                                                            placeholder={col.type === 'time' ? getTimeFormatPlaceholder() : ''}
                                                            autoFocus
                                                        />
                                                    )
                                                ) : (
                                                    <div 
                                                        className="px-2 py-1 cursor-pointer hover:bg-muted rounded min-h-[32px] text-sm"
                                                        onClick={() => setEditingCell(`${rowIndex}-${col.id}`)}
                                                    >
                                                        {row[col.id] || (
                                                            <span className="text-muted-foreground italic">
                                                                {col.type === 'time' ? getTimeFormatPlaceholder() : 'Click to edit'}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        ))}
                                        <td className="p-2">
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                                onClick={() => deleteRow(rowIndex)}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* AI Query Section */}
            <div className="mt-6 card-default p-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-accent/10">
                        <Wand2 className="h-5 w-5 text-accent" />
                    </div>
                    <div>
                        <h3 className="font-semibold">AI Plot Assistant</h3>
                        <p className="text-sm text-muted-foreground">Ask questions about your plot, get suggestions, or brainstorm ideas</p>
                    </div>
                    <div className="ml-auto text-sm text-muted-foreground">
                        {user?.credits || 0} credits
                    </div>
                </div>

                <div className="flex gap-3">
                    <Textarea
                        value={aiQuery}
                        onChange={(e) => setAiQuery(e.target.value)}
                        placeholder="Ask anything about your plot... e.g., 'How can I increase tension in Chapter 3?' or 'What subplot could connect these two characters?'"
                        rows={2}
                        className="flex-1"
                        data-testid="ai-plot-query"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleAiQuery();
                            }
                        }}
                    />
                    <Button 
                        onClick={handleAiQuery} 
                        disabled={aiLoading || !aiQuery.trim()}
                        className="self-end"
                        data-testid="ai-plot-query-btn"
                    >
                        {aiLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="h-4 w-4" />
                        )}
                    </Button>
                </div>

                {/* AI Response */}
                {aiResponse && (
                    <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                        <div className="flex items-start gap-3">
                            <Sparkles className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                            <div className="prose prose-sm max-w-none">
                                <p className="whitespace-pre-wrap">{aiResponse}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Query History */}
                {queryHistory.length > 0 && (
                    <div className="mt-4">
                        <button 
                            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                            onClick={() => {
                                const historyDiv = document.getElementById('query-history');
                                if (historyDiv) historyDiv.classList.toggle('hidden');
                            }}
                        >
                            <Clock className="h-3 w-3" />
                            Previous questions ({queryHistory.length})
                        </button>
                        <div id="query-history" className="hidden mt-3 space-y-3 max-h-64 overflow-y-auto">
                            {queryHistory.map((item) => (
                                <div key={item.id} className="p-3 bg-muted/30 rounded-lg text-sm">
                                    <div className="font-medium text-accent mb-1">Q: {item.query}</div>
                                    <div className="text-muted-foreground line-clamp-3">{item.response}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Help Text */}
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-semibold text-sm mb-2">Tips:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Click any cell to edit its content</li>
                    <li>• Click column headers to rename them</li>
                    <li>• Use arrow buttons under headers to reorder columns</li>
                    <li>• Add custom columns for character arcs, themes, or any tracking you need</li>
                    <li>• Use "Copy for Sheets" to paste directly into Google Sheets or Excel</li>
                    <li>• Ask the AI assistant for plot advice, pacing suggestions, or brainstorming help</li>
                </ul>
            </div>
        </div>
    );
};

export default PlotGrid;
